<template>
  <VDataGridV2
    class="preorder-positions-table"
    :data="positions"
    sticky
    layout="fill"
  >
    <VDataGridColumnV2
      id="manufacturer"
      width="17%"
    >
      <template #label>
        Производитель
        <div class="description">
          Юр. лицо поставщика
        </div>
      </template>
      <template #default="{ row }">
        <div
          class="title"
          :data-mismatch="row.getManufacturerMismatch(product.manufacturers)"
        >
          {{ row.manufacturer.name }}
        </div>

        <div class="title description">
          Юр. лицо: {{ row.sellerCompany.name }}
        </div>

        <div class="description">
          Поставщик: {{ row.seller.name }}
        </div>

        <div
          class="description"
          :data-mismatch="product.hasExpirationRequirement && row.getExpirationDateMismatch(product.requiredExpirationDate)"
        >
          <span class="nowrap">
            Годен до: {{ $core.utils.date.humanizeDate(row.productExpirationDate, null) }}
          </span>
        </div>
      </template>
    </VDataGridColumnV2>

    <VDataGridColumnV2
      id="characteristics"
      label="Характеристики"
    >
      <template #default="{ row }">
        <div
          v-if="$core.utils.isNotEmptyArray(row.offer.characteristics)"
          class="characteristics"
        >
          <div
            v-for="(charac, index) in row.offer.characteristics"
            :key="row.id + '_' + charac.characteristic.id + '_' + index"
            class="characteristics-item title"
            :class="{ 'up': index >= 5 }"
            :data-mismatch="charac.getMismatchByProduct(product)"
          >
            {{ charac.name }}: {{ charac.valueStringRepresentation }}
          </div>
        </div>
        <div class="footnote">
          Описание: {{ row.description || '--' }}
        </div>
      </template>
    </VDataGridColumnV2>

    <VDataGridColumnV2
      id="receiptPeriod"
      label="Интервал получения"
      width="12%"
    >
      <template #default="{ row }">
        <div class="title">
          <span class="nowrap">
            {{ row.receiptPeriod.toString() }}
          </span>
        </div>
      </template>
    </VDataGridColumnV2>

    <VDataGridColumnV2
      id="qty"
      label="Доступно"
      width="12%"
      content-align="right"
    >
      <template #default="{ row }">
        <div class="title">
          <span class="nowrap">
            {{ row.qty.format(row.unit) }}
          </span>
        </div>
        <div class="description">
          <span class="nowrap">
            Фасовка: {{ row.packageVolume?.format(row.unit) }}
          </span>
        </div>
      </template>
    </VDataGridColumnV2>

    <VDataGridColumnV2
      id="price"
      width="200px"
      content-align="right"
    >
      <template #label>
        Цена с НДС + доставка
        <div class="description">
          Тип доставки
        </div>
      </template>
      <template #default="{ row }">
        <div
          class="price nowrap"
          @mouseenter="$core.tooltip.show($event.currentTarget, priceTooltip(row))"
          @mouseleave="$core.tooltip.hide()"
        >
          {{ row.priceFullSeller.formatInDisplayCurrency(row.currencyCode, row.getDateForCurrencyRate(), row.unit) }}
        </div>
        <span
          v-if="row.currencyCode !== $core.displayedCurrencyCode"
          class="description"
        >
          Пересчитано из
          {{ row.priceFullSeller.format(row.currencyCode, row.unit) }}
        </span>
        <div class="description">
          {{ row.deliveryType.name }}
        </div>
      </template>
    </VDataGridColumnV2>

    <VDataGridColumnV2
      id="actions"
      width="4%"
      content-align="right"
    >
      <template #default="{ row }">
        <VButton
          :disabled="!row.qty.isPositive()"
          :title="row.qty.isPositive() ? 'Оформить предзаказ' : 'Предложение исчерпано'"
          @click="openBuyPopup($event, row)"
        >
          <template #icon>
            <VIcon material="add_shopping_cart"/>
          </template>
        </VButton>
      </template>
    </VDataGridColumnV2>
  </VDataGridV2>

  <BuyOneClickPopup
    :visible="popup.target !== null"
    :entity="popup.entity"
    :target="popup.target"
    :is-save-disabled="popup.isSaveDisabled"
    @close="closeBuyPopup"
    @start-create-order="popup.isSaveDisabled = true"
    @cancel="popup.isSaveDisabled = false"
    @create-order="createOrder"
  />
</template>

<script lang="ts">
import type Product        from '@/domains/Products/Product';
import type AbstractDemand from '@/domains/Abstract/AbstractDemand';
import Order               from '@/domains/Orders/Order';
import OrdersService       from '@/domains/Orders/OrdersService';
import PreorderPosition    from '@/domains/Preorders/PreorderPosition';
import BuyOneClickPopup    from '@/domains/Preorders/components/BuyOneClickPopup/BuyOneClickPopup.vue';

/**
 * Позиции чужих предзаказов, к которым покупатель может присоединиться.
 *
 * Эталон приходит одним объектом: тендер и потребность оба наследуют
 * AbstractDemand и несут товар, с требованиями которого сравниваем.
 *
 * Расхождения подсвечиваются атрибутом data-mismatch — точку и подсказку
 * рисует CSS диалога. Покупка оформляется здесь же: снаружи о попапе знать
 * незачем, наружу уходит только факт сохранения.
 */
export default defineComponent({
  name: 'PreorderPositionsTable',

  components: {
    BuyOneClickPopup,
  },

  props: {
    positions: {
      type:     Array as PropType<PreorderPosition[]>,
      required: true,
    },

    /** С чем сравниваем: тендер или потребность. */
    instanceOfAbstractDemand: {
      type:     Object as PropType<AbstractDemand>,
      required: true,
    },
  },

  emits: ['save'],

  data() {
    return {
      /** Попап «купить в один клик». Открыт, пока задан target. */
      popup: {
        target:         null as EventTarget | null,
        entity:         null as Order | null,
        isSaveDisabled: false,
      },
    };
  },

  computed: {
    /** Товар-эталон: производители, срок годности, рамки характеристик. */
    product(): Product {
      return this.instanceOfAbstractDemand.product;
    },
  },

  methods: {
    /** Тултип цены поставщика: цена + пересчёт из валюты позиции. */
    priceTooltip(row: PreorderPosition): string {
      const lines = [
        `Цена: ${row.priceSeller.formatInDisplayCurrency(row.currencyCode, row.getDateForCurrencyRate(), row.unit)}`,
      ];

      if (row.currencyCode !== this.$core.displayedCurrencyCode) {
        lines.push(`Пересчитано из: ${row.priceSeller.format(row.currencyCode, row.unit)}`);
      }

      return lines.join('\n');
    },

    /**
     * FIXME конструкторы отсюда переезжают в BuyOneClickPopup: попапу нужно
     * отдавать саму позицию предзаказа, а заказ из неё он соберёт сам.
     */
    openBuyPopup(event: Event, preorderPosition: PreorderPosition): void {
      this.popup.entity = Order.fromPreorderPosition(preorderPosition);
      this.popup.target = event.target;
    },

    closeBuyPopup(): void {
      this.popup.target         = null;
      this.popup.entity         = null;
      this.popup.isSaveDisabled = false;
    },

    /** Создаёт заказ через сервис (нотификация — внутри сервиса) */
    async createOrder(): Promise<void> {
      const success = await OrdersService.add(this.popup.entity!);

      if (success) {
        this.closeBuyPopup();
        this.$emit('save');
      }
    },
  },
});
</script>